// Sends room close commands only (cleanup handled by event-driven flow)

import { useState, useCallback } from 'react';
import { useRoute } from './useRoute';
import { useRoomConfig } from './useRoomConfig';
import { useRoomSocket } from './useRoomSocket';
import {
  createLogger,
  generateCommandId,
} from '@airhead-games/shared/utilities';
import {
  RoomCommand,
  RoomActionType,
  SenderRoleType,
} from '@airhead-games/shared/types';

const logger = createLogger('useRoomClose');
export interface UseRoomCloseReturn {
  closeRoom: () => Promise<void>;
  isClosing: boolean;
}

export const useRoomClose = (): UseRoomCloseReturn => {
  const { isPlayingRoute } = useRoute();
  const { roomCode, host } = useRoomConfig();
  const { sendCommand } = useRoomSocket();
  const [isClosing, setIsClosing] = useState(false);

  const closeRoom = useCallback(async (): Promise<void> => {
    if (isPlayingRoute) {
      logger.warn('closeRoom() cannot be called by players');
      return;
    }

    if (isClosing) {
      logger.warn('Room close already in progress, ignoring duplicate call');
      return;
    }

    if (!roomCode || !host) {
      logger.error('No roomCode or host found, cannot close room');
      return;
    }

    logger.debug(`Starting room close sequence for room ${roomCode}...`);
    setIsClosing(true);

    try {
      // Send CLOSE command to backend - cleanup will be handled by event-driven flow
      const command: RoomCommand = {
        id: generateCommandId(),
        type: 'RoomAction',
        action: RoomActionType.CLOSE,
        senderRole: SenderRoleType.HOST,
        clientTimestamp: Date.now(),
        roomCode,
        host,
      };

      logger.debug('Sending room close command...');
      sendCommand(command);
      logger.debug('Room close command sent successfully');

      setIsClosing(false);
    } catch (error) {
      logger.error('Error during room close command:', error);
      setIsClosing(false);
      throw error;
    }
  }, [isPlayingRoute, isClosing, roomCode, host, sendCommand]);

  return {
    closeRoom,
    isClosing,
  };
};
